import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Ola · Combinaciones.
 *
 * Cada combinación es un formulario que funciona sin JavaScript (envía `items[][id]` y
 * `items[][quantity]` a /cart/add). Este componente:
 * - recalcula el total cuando cambia el color o el aroma de un producto;
 * - agrega la combinación completa en una sola petición, con el mismo flujo y los mismos
 *   eventos que el formulario de producto del tema, así el carrito se actualiza y se abre.
 *
 * Atributos del componente:
 * - `data-money-format`: formato de moneda de la tienda (`shop.money_format`).
 * - `data-currency`: moneda del carrito.
 *
 * @extends {Component}
 */
export class OlaCombinaciones extends Component {
  /** Tiempo que se ve el mensaje de «Agregado». */
  static STATUS_DURATION = 4000;

  /** @type {WeakMap<HTMLFormElement, number>} */
  #statusTimers = new WeakMap();

  /**
   * Recalcula el total del formulario. Se llama desde `on:change`.
   * @param {Event} event
   */
  updateTotal(event) {
    const form = event.target instanceof Element ? event.target.closest('form') : null;
    if (!(form instanceof HTMLFormElement)) return;

    const total = this.#selectedItems(form).reduce((sum, item) => sum + item.price, 0);
    const output = form.querySelector('[data-total]');
    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (output && format && currency) output.textContent = formatMoney(total, format, currency);
  }

  /**
   * Agrega toda la combinación al carrito. Se llama desde `on:submit`.
   * @param {SubmitEvent} event
   */
  addCombo(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const items = this.#selectedItems(form);
    if (items.length === 0) return;

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const itemCount = items.length;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((item) => ({ id: Number(item.id), quantity: 1 })),
        sections: sectionIds.join(','),
      }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const failed = Boolean(response.status);

        if (failed) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          this.#setStatus(form, response.description || response.message || '');
        } else {
          this.#setStatus(form, Theme.translations.added);
        }

        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'ola-combinaciones',
            sourceId: form.id,
            itemCount,
            sections: failed ? undefined : response.sections,
            didError: failed,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        button?.removeAttribute('aria-busy');
      });
  }

  /**
   * Variantes elegidas en el formulario, con su precio en centavos.
   * @param {HTMLFormElement} form
   * @returns {{ id: string, price: number }[]}
   */
  #selectedItems(form) {
    /** @type {{ id: string, price: number }[]} */
    const items = [];
    for (const field of form.querySelectorAll('[name="items[][id]"]')) {
      if (field instanceof HTMLSelectElement) {
        const option = field.selectedOptions[0];
        if (option) items.push({ id: option.value, price: Number(option.dataset.price) || 0 });
      } else if (field instanceof HTMLInputElement) {
        items.push({ id: field.value, price: Number(field.dataset.price) || 0 });
      }
    }
    return items;
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      const fetchCartData = /** @type {any} */ (cartItems).fetchCartData;
      if (typeof fetchCartData === 'function') return fetchCartData.call(cartItems);
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Muestra un mensaje en la zona de estado del formulario (se anuncia a lectores de pantalla).
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #setStatus(form, message) {
    const status = form.querySelector('[data-status]');
    if (!status) return;

    window.clearTimeout(this.#statusTimers.get(form));
    status.textContent = message;
    this.#statusTimers.set(
      form,
      window.setTimeout(() => {
        status.textContent = '';
      }, OlaCombinaciones.STATUS_DURATION)
    );
  }
}

if (!customElements.get('ola-combinaciones')) {
  customElements.define('ola-combinaciones', OlaCombinaciones);
}
